import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaMapMarkerAlt } from 'react-icons/fa';

import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import EmployeeDashboardLayout from './EmployeeDashboardLayout';
import {
  isEmployeeDeliveryAgent,
  resolveDeliveryAgentOrderSourceFromState,
} from './deliveryAgentSession';
import { fetchGroceryOrderDetail, fetchGroceryOrderPayments } from './groceryApi';
import { buildAddressMapUrl, formatSchoolAddressFromParts } from './orderApi';
import { getStoreOrderChannel, storeOrderSourceToChannel, isRestaurantStoreOrderSource } from './menuChannels';
import { formatMobileDisplayOrFallback } from './formatDisplay';
import OrderStatusPill from './ui/OrderStatusPill';
import { storeOrderStatusChannel } from './ui/orderStatusFlow';
import {
  formatDisplayableExpectedDelivery,
  formatStoreOrderDateTime,
} from './storeOrderDateUtils';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Inventory.css';

const displayValue = (value) => {
  const text = value != null ? String(value).trim() : '';
  return text || '—';
};

function formatMoney(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toFixed(2)}`;
}

function formatDate(raw) {
  return formatStoreOrderDateTime(raw);
}

function isSuccessfulPaymentStatus(status) {
  const norm = String(status ?? '').trim().toUpperCase();
  return (
    norm === 'SUCCESS' ||
    norm === 'COMPLETED' ||
    norm === 'COMPLETE' ||
    norm === 'PAID' ||
    norm === 'SUCCEEDED'
  );
}

function buildOrdersBackPath(location, order) {
  const orderType =
    order?.orderSource != null && String(order.orderSource).trim() !== ''
      ? storeOrderSourceToChannel(order.orderSource)
      : location.state?.fromOrderType || 'pos';
  return `/home?tab=orders&orderType=${orderType}`;
}

const DetailField = ({ label, value, wide = false, children }) => (
  <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
    <span className="restaurant-detail-label">{label}</span>
    {children ?? <span className="restaurant-detail-value">{displayValue(value)}</span>}
  </div>
);

const StoreOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const rawId = orderId != null ? String(orderId).trim() : '';
    const idNum = Number(rawId);

    if (!rawId || Number.isNaN(idNum)) {
      setLoading(false);
      setError('Invalid order id.');
      setOrder(null);
      return undefined;
    }

    (async () => {
      setLoading(true);
      setError('');
      setOrder(null);
      setPayments([]);
      try {
        const [detail, paymentRows] = await Promise.all([
          fetchGroceryOrderDetail(idNum),
          fetchGroceryOrderPayments(idNum),
        ]);
        if (cancelled) return;
        if (!detail) {
          setError('Order not found.');
          return;
        }
        setOrder(detail);
        setPayments(Array.isArray(paymentRows) ? paymentRows : []);
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            'Failed to load order details.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const lineItems = useMemo(
    () => order?.lineItems ?? order?.orderLineItems ?? [],
    [order]
  );

  const orderLabel =
    order?.displayOrderRef ?? order?.groceryOrderId ?? order?.orderId ?? orderId;

  const isRestaurantOrder =
    isRestaurantStoreOrderSource(order?.orderSource) ||
    location.state?.fromOrderType === 'restaurant';

  const customerDisplay = useMemo(() => {
    if (isRestaurantOrder) {
      const name = order?.restaurantName ?? order?.Restaurant_Name;
      return name || order?.receiverName || '—';
    }
    const name = order?.receiverName;
    const phone = formatMobileDisplayOrFallback(order?.receiverPhone, '');
    if (name && phone) return `${name} · ${phone}`;
    return name || phone || '—';
  }, [order, isRestaurantOrder]);

  const partyFieldLabel = isRestaurantOrder ? 'Company' : 'Customer';

  const orderChannel = useMemo(() => {
    const fromSource =
      order?.orderSource != null && String(order.orderSource).trim() !== ''
        ? storeOrderSourceToChannel(order.orderSource)
        : null;
    const key = fromSource ?? location.state?.fromOrderType ?? 'pos';
    return getStoreOrderChannel(key).key;
  }, [order, location.state?.fromOrderType]);

  const isDeliveryOrder =
    order && String(order?.orderType ?? 'DELIVERY').toUpperCase() !== 'PICKUP';

  const hasDeliveryAgentInfo = Boolean(
    order?.deliveryUserId || order?.deliveryUserName || order?.deliveryUserPhone
  );

  const deliveryAddressDisplay = useMemo(() => {
    if (!order) return '';
    const formatted = String(order.deliveryAddressFormatted ?? '').trim();
    if (formatted) return formatted;
    return formatSchoolAddressFromParts(order.deliveryAddress) || '';
  }, [order]);

  const deliveryMapUrl = useMemo(
    () => buildAddressMapUrl(order?.deliveryAddress, deliveryAddressDisplay),
    [order?.deliveryAddress, deliveryAddressDisplay]
  );

  const showDeliverySection =
    isDeliveryOrder &&
    (orderChannel === 'myeats' ||
      orderChannel === 'restaurant' ||
      hasDeliveryAgentInfo ||
      Boolean(deliveryAddressDisplay || order?.deliveryAddress));

  const successPayments = useMemo(
    () => payments.filter((p) => isSuccessfulPaymentStatus(p.status)),
    [payments]
  );

  const handleSelectOrderChannel = (key) => {
    navigate(`/home?tab=orders&orderType=${getStoreOrderChannel(key).key}`);
  };

  const isDeliveryAgent = isEmployeeDeliveryAgent();
  const deliveryOrderSource = resolveDeliveryAgentOrderSourceFromState(
    location.state,
    order ?? {}
  );
  const backPath = isDeliveryAgent
    ? '/business/user'
    : buildOrdersBackPath(location, order);

  const wrapDetailPage = (content) => {
    if (isDeliveryAgent) {
      return (
        <EmployeeDashboardLayout deliveryOrderSource={deliveryOrderSource}>
          {content}
        </EmployeeDashboardLayout>
      );
    }
    return (
      <RestaurantDashboardLayout
        activeTab="orders"
        orderChannel={orderChannel}
        onSelectOrderChannel={handleSelectOrderChannel}
      >
        {content}
      </RestaurantDashboardLayout>
    );
  };

  return wrapDetailPage(
      <div className="purchase-detail-page store-order-detail-page">
        <div className="restaurant-detail-panel">
          <div className="restaurant-detail-card">
            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
              <div className="inventory-item-detail-header-row">
                <button
                  type="button"
                  className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                  onClick={() =>
                    navigate(backPath, { state: { deliveryOrderSource } })
                  }
                  aria-label="Back to orders"
                >
                  <FaArrowLeft aria-hidden />
                </button>
                <h3 className="restaurant-detail-section-title">Order #{orderLabel}</h3>
              </div>
            </div>

            {loading ? <p className="order-details-loading">Loading order details…</p> : null}
            {!loading && error ? <p className="restaurant-detail-message error">{error}</p> : null}

            {!loading && !error && order ? (
              <div className="restaurant-detail-grid">
                <DetailField label="Status">
                  <OrderStatusPill
                    status={order.orderStatusCode || order.orderStatus}
                    channel={storeOrderStatusChannel(orderChannel, order)}
                  />
                </DetailField>
                <DetailField label="Store" value={order.storeName || order.storeId} />
                <DetailField label="Source" value={order.orderSource} />
                <DetailField label="Type" value={order.orderType} />
                <DetailField label={partyFieldLabel} value={customerDisplay} wide />
                {order.masterOrderId ? (
                  <DetailField label="Parent order" value={`#${order.masterOrderId}`} />
                ) : null}
                <DetailField label="Subtotal" value={formatMoney(order.subTotal)} />
                <DetailField label="GST" value={formatMoney(order.gst ?? order.gstTotal)} />
                {(Number(order.deliveryFee) || 0) > 0 ? (
                  <DetailField label="Delivery fee" value={formatMoney(order.deliveryFee)} />
                ) : null}
                {(Number(order.convienceFee) || 0) > 0 ? (
                  <DetailField label="Convenience fee" value={formatMoney(order.convienceFee)} />
                ) : null}
                {(Number(order.platformFee) || 0) > 0 ? (
                  <DetailField label="Platform fee" value={formatMoney(order.platformFee)} />
                ) : null}
                <DetailField label="Total" value={formatMoney(order.grandTotal ?? order.totalPrice)} />
                <DetailField label="Created on" value={formatDate(order.createdDate)} />
                
              </div>
            ) : null}

            {!loading && !order && !error ? (
              <p className="order-details-empty">No detail data for this order.</p>
            ) : null}
          </div>

          {!loading && !error && order ? (
            <div className="restaurant-detail-card restaurant-detail-card--line-items">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Line items</h3>
              </div>
              {lineItems.length === 0 ? (
                <p className="inventory-items-hint">No line items for this order.</p>
              ) : (
                <div className="purchase-detail-lines-table-wrap">
                  <table className="purchase-detail-lines-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qty</th>
                        <th className="purchase-detail-lines-table__num">Price</th>
                        <th className="purchase-detail-lines-table__num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((li) => (
                        <tr key={li.lineItemId ?? `${li.itemName}-${li.quantity}`}>
                          <td>{displayValue(li.itemName)}</td>
                          <td>{li.quantity ?? '—'}</td>
                          <td className="purchase-detail-lines-table__num">
                            {formatMoney(li.unitPrice)}
                          </td>
                          <td className="purchase-detail-lines-table__num">
                            {formatMoney(li.totalPrice)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {!loading && !error && order && showDeliverySection ? (
            <div className="restaurant-detail-card">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Delivery</h3>
              </div>
              <div className="restaurant-detail-grid">
                <DetailField label="Receiver name" value={order.receiverName} />
                <DetailField
                  label="Receiver phone"
                  value={formatMobileDisplayOrFallback(order.receiverPhone)}
                />
                <DetailField label="Delivery agent name" value={order.deliveryUserName} />
                <DetailField
                  label="Delivery agent phone"
                  value={formatMobileDisplayOrFallback(order.deliveryUserPhone)}
                />
                {order.deliveryUserId ? (
                  <DetailField label="Delivery agent ID" value={order.deliveryUserId} />
                ) : null}
                <DetailField
                  label="Expected delivery"
                  value={formatDisplayableExpectedDelivery(order)}
                />
                {deliveryAddressDisplay || order.deliveryAddress ? (
                  <div className="restaurant-detail-field restaurant-detail-field-wide">
                    <span className="restaurant-detail-label">Delivery address</span>
                    <span className="restaurant-detail-value order-details-address-row">
                      {displayValue(deliveryAddressDisplay)}
                      {deliveryMapUrl ? (
                        <a
                          href={deliveryMapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="order-details-map-icon-link"
                          aria-label="Open address in Google Maps"
                          title="Open in Google Maps"
                        >
                          <FaMapMarkerAlt aria-hidden />
                        </a>
                      ) : null}
                    </span>
                  </div>
                ) : null}
                {order.deliveryOtp ? (
                  <DetailField label="Delivery OTP" value={order.deliveryOtp} />
                ) : null}
              </div>
            </div>
          ) : null}

          {!loading && !error && successPayments.length > 0 ? (
            <div className="restaurant-detail-card restaurant-detail-card--line-items">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Payment details</h3>
              </div>
              {successPayments.map((p, index) => (
                <div
                  key={p.paymentId ?? `payment-${index}`}
                  className={`restaurant-detail-grid${successPayments.length > 1 ? ' store-order-payment-block' : ''}`}
                >
                  {successPayments.length > 1 ? (
                    <DetailField label="Payment" value={`#${index + 1}`} wide />
                  ) : null}
                  <DetailField label="Amount" value={formatMoney(p.amount)} />
                  <DetailField label="Payment mode" value={displayValue(p.paymentMode)} />
                  <DetailField label="Transaction ID" value={displayValue(p.transactionId)} wide />
                  <DetailField label="Bank RRN" value={displayValue(p.bankRrn)} />
                  <DetailField label="Paid on" value={formatDate(p.createdDate)} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
  );
};

export default StoreOrderDetail;
